import { render } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { modal } from "../../ui/modal";
import { IMG_ROOT } from "../../lib/constants";
import { optinartwork } from "../miscOperations";
import { config } from "../../config";
import { ScoreBoxes, StatusDisplay } from "./DirectoryStatus";
import {
  isActivelyEdited,
  isArtistEdited,
  markArtistEdited,
  onAiMetadata,
} from "../aiMetadataLive";

interface DirectoryArtwork {
  id: number;
  imgFile: string;
  tableSuffix: string;
  title: string;
  stage: string;
  category: number;
  megapixels: number | null;
  needsOptIn: boolean;
  isTestAccount: boolean;
  score: number;
}

interface DirectoryCategory {
  id: number;
  name: string;
  group: string;
}

function DirectoryArtworkCard({
  artwork,
  categories,
  onUpdate,
}: {
  artwork: DirectoryArtwork;
  categories: DirectoryCategory[];
  onUpdate: (id: number, field: string, value: string) => void;
}) {
  const { id, imgFile, title, stage, category, megapixels, needsOptIn, isTestAccount, score } =
    artwork;

  const illustrationOptions = categories.filter((c) => c.group === "illustration");
  const photographyOptions = categories.filter((c) => c.group === "photography");

  /**
   * Live-fill from the AI metadata run the uploader kicked off for this artwork.
   *
   * Both layouts read the same DOM, so patching these three nodes covers both: thumbnail
   * view shows only the status title, list view adds the title input and the category
   * select.
   *
   * Written into the DOM rather than kept as state, deliberately. Preact force-syncs a
   * `value` prop, so re-rendering to show an AI category would also reset the sibling title
   * input to its last saved value — wiping whatever the artist was mid-word in typing,
   * which is the exact thing the guards below exist to prevent.
   *
   * The guards are per field: a title they wrote themselves does not stop the category
   * being filled in, and vice versa. `isArtistEdited` covers a field they have already left,
   * `isActivelyEdited` the one they are in right now.
   */
  const statusTitleRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    return onAiMetadata(id, (result) => {
      const titleEl = titleRef.current;
      if (result.title && !isArtistEdited(id, "title") && !isActivelyEdited(titleEl)) {
        if (titleEl) titleEl.value = result.title;
        if (statusTitleRef.current) statusTitleRef.current.textContent = result.title;
      }

      const catEl = categoryRef.current;
      if (
        result.categoryId != null &&
        catEl &&
        !isArtistEdited(id, "category") &&
        !isActivelyEdited(catEl)
      ) {
        // Only when the option is actually in this list: assigning an absent value leaves
        // selectedIndex at -1, so the select would read as "no category" to the artist
        // while the server has one saved.
        const wanted = String(result.categoryId);
        if (Array.from(catEl.options).some((o) => o.value === wanted)) catEl.value = wanted;
      }
    });
  }, [id]);

  const scoreBlock = needsOptIn ? (
    <div style="font-size: 0.8em; text-align: center;">
      <b>Expanded products</b> &nbsp; &nbsp;{" "}
      <span class="button small green" onClick={() => void optinartwork(String(id))}>
        OPT-IN
      </span>
    </div>
  ) : (
    <ScoreBoxes score={score} />
  );

  return (
    <>
      <div class="directory-artwork-img">
        <div
          class="directory-artwork-image"
          style={`background-image: url('${IMG_ROOT}${imgFile}'); cursor: pointer;`}
          onClick={() => {
            modal(`directoryedit:${id}:`, "large");
          }}
        />
      </div>
      <div class="directory-artwork-status">
        <div class="directory-artwork-status-title" ref={statusTitleRef}>
          {title ?? "Untitled"}
        </div>
        <StatusDisplay stage={stage} megapixels={megapixels} />
      </div>
      {needsOptIn && (
        <div class="directory-artwork-expbutton" onClick={() => void optinartwork(String(id))}>
          <span class="exp-button">Expanded licensing OPT-IN</span>
        </div>
      )}
      <div class="directory-artwork-title">
        <input
          type="text"
          class="directory-input"
          id={`artwork-title-${id}`}
          ref={titleRef}
          value={title}
          placeholder="Artwork Title"
          onInput={() => {
            // "Started writing" — not `onBlur`, which fires even when they only tabbed
            // through and would suppress a perfectly good AI title.
            markArtistEdited(id, "title");
          }}
          onBlur={(e) => {
            onUpdate(id, "title", (e.target as HTMLInputElement).value);
          }}
        />
      </div>
      <div class="directory-artwork-category">
        <select
          class="select uploaderCatSelect"
          id={`artwork-category-${id}`}
          ref={categoryRef}
          onChange={(e) => {
            markArtistEdited(id, "category");
            onUpdate(id, "category", (e.target as HTMLSelectElement).value);
          }}
        >
          <option value="">-- Select category --</option>
          <optgroup label="Illustration">
            {illustrationOptions.map((c) => (
              <option key={c.id} value={String(c.id)} selected={category === c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Photography">
            {photographyOptions.map((c) => (
              <option key={c.id} value={String(c.id)} selected={category === c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
        </select>
      </div>
      <div class="directory-artwork-listing">
        <select
          class="select"
          id={`artwork-listing-${id}`}
          disabled={config.hiddenSale}
          onChange={(e) => {
            onUpdate(id, "status", (e.target as HTMLSelectElement).value);
          }}
        >
          <option
            value="META_DATA_CORRECT"
            selected={config.hiddenSale || stage !== "READY_FOR_SALE"}
          >
            Not for sale
          </option>
          {!isTestAccount && !config.hiddenSale && (
            <option value="READY_FOR_SALE" selected={stage === "READY_FOR_SALE"}>
              For sale
            </option>
          )}
        </select>
      </div>
      <div class="directory-artwork-score">{scoreBlock}</div>
      <div class="directory-artwork-buttons">
        <span
          class="button medium lilac"
          onClick={() => {
            modal(`directoryedit:${id}:`, "large");
          }}
        >
          Edit
        </span>
      </div>
    </>
  );
}

/**
 * Render a single directory artwork card into its container.
 * Used by directoryRefresh() after the API returns updated artwork data.
 */
export function mountDirectoryCard(
  container: HTMLElement,
  artwork: DirectoryArtwork,
  categories: DirectoryCategory[],
  onUpdate: (id: number, field: string, value: string) => void
): void {
  render(
    <DirectoryArtworkCard artwork={artwork} categories={categories} onUpdate={onUpdate} />,
    container
  );
}
